import { Location } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { selectSelectedAccountId } from '@mkp/account/state';
import { MessageError, getMessageError } from '@mkp/shared/data-access';
import { UUID, toUUID } from '@mkp/shared/models';
import { ActionState } from '@mkp/shared/ui-library';
import { SnackbarService } from '@mkp/shared/util-snackbar';
import { AppConfig } from '@mkp/shared/util-app-config';
import { QueryOptions } from '@mkp/shared/util-pagination';
import { WorkspaceRouteService } from '@mkp/shared/util-router';
import { BoDashboardActions } from '@mkp/user/feature-bo-dashboard/actions';
import { BoDashboardService } from '@mkp/user/util-bo-dashboard';
import { VacancyDetailResource, VacancySerializer, VacancyService } from '@mkp/vacancy/data-access';
import { vacancyEditActions } from '@mkp/vacancy/feature-vacancy-edit/actions';
import { Vacancy } from '@mkp/vacancy/models';
import {
  vacancyActions as VacancyActions,
  listVacancyActions,
  vacancyApiActions,
  vacancyExistsGuardActions,
  vacancyFormPageActions,
  vacancySearchExistsGuardActions,
  vacancyWithoutPaidProductGuardActions,
} from '@mkp/vacancy/state/actions';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
  Observable,
  OperatorFunction,
  catchError,
  concatMap,
  filter,
  map,
  mergeMap,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';

@Injectable()
export class VacancyEffects {
  private readonly actions$ = inject(Actions);
  private readonly vacancySvc = inject(VacancyService);
  private readonly notificationSvc = inject(SnackbarService);
  private readonly translateSvc = inject(TranslateService);
  private readonly location = inject(Location);
  private readonly vacancyDetailResource = inject(VacancyDetailResource);
  private readonly boDashboardService = inject(BoDashboardService);
  private readonly store = inject(Store);
  private readonly workspaceRoute = inject(WorkspaceRouteService);

  createVacancy$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(vacancyEditActions.createVacancy),
        switchMap(({ vacancy }) => {
          return this.vacancySvc.createVacancy(vacancy).pipe(
            tap(() => this.notify('VACANCY_PAGE.MESSAGE.CREATE')),
            tap((vacancy) => this.boDashboardService.addVacancy(vacancy)),
            map((vacancy) => VacancyActions.CreateVacancySuccess({ vacancy })),
            catchError((error: unknown) =>
              of(VacancyActions.CreateVacancyFailure({ error: getVacancyError(error) }))
            )
          );
        })
      ),
    { useEffectsErrorHandler: false }
  );

  // CDP-432 - after vacancy creation: browser back button should redirect to /edit instead of /create
  updateVacancyCreationHistory$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(VacancyActions.CreateVacancySuccess),
        tap(({ vacancy }) => {
          const url = this.workspaceRoute.serializeUrl(
            AppConfig.routes.vacancy,
            vacancy.id,
            AppConfig.routes.publish,
            AppConfig.routes.edit
          );
          if (url) {
            this.location.replaceState(url);
          }
        })
      ),
    { dispatch: false }
  );

  // From Edit page
  updateVacancy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(vacancyFormPageActions.updateVacancy),
      switchMap(({ vacancy }) => {
        return this.vacancySvc.updateVacancy(vacancy).pipe(
          this.updateVacancySuccessOperator(),
          map(({ id, changes }) => {
            return vacancyApiActions.updateVacancySuccess({ update: { id, changes } });
          }),
          catchError((error: unknown) =>
            of(
              vacancyApiActions.updateVacancyFailure({
                vacancyId: vacancy.id,
                error: getVacancyError(error),
              })
            )
          )
        );
      })
    )
  );

  listVacancies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(listVacancyActions.load),
      concatLatestFrom(() =>
        this.store.select(selectSelectedAccountId).pipe(filter(Boolean), take(1))
      ),
      this.fetchVacancies(
        vacancyApiActions.listVacanciesSuccess,
        vacancyApiActions.listVacanciesFailure
      )
    )
  );

  loadMoreVacancies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(listVacancyActions.loadMore),
      concatLatestFrom(() =>
        this.store.select(selectSelectedAccountId).pipe(filter(Boolean), take(1))
      ),
      this.fetchVacancies(
        vacancyApiActions.loadMoreVacanciesSuccess,
        vacancyApiActions.loadMoreVacanciesFailure
      )
    )
  );

  /*** Get Vacancy ***/
  getVacancy$ = createEffect(
    () =>
      this.actions$.pipe(ofType(VacancyActions.GetVacancy)).pipe(
        mergeMap(({ vacancyId }) => {
          return this.vacancySvc.read(vacancyId).pipe(
            map((vacancy) => VacancyActions.GetVacancySuccess({ vacancy })),
            catchError((error: unknown) => {
              return of(
                VacancyActions.GetVacancyFailure({ vacancyId, error: getVacancyError(error) })
              );
            })
          );
        })
      ),
    { useEffectsErrorHandler: false }
  );

  readonly loadVacancyFromGuard$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        vacancyExistsGuardActions.canActivate,
        vacancyWithoutPaidProductGuardActions.canActivate
      ),
      concatMap(({ vacancyId }) => {
        return this.vacancyDetailResource.getVacancy(toUUID(vacancyId)).pipe(
          map((vacancy) => vacancyApiActions.loadVacancyFromGuardSuccess({ vacancy })),
          catchError((error: unknown) =>
            of(
              vacancyApiActions.loadVacancyFromGuardFailure({
                vacancyId,
                error: getVacancyError(error),
              })
            )
          )
        );
      })
    );
  });

  readonly searchVacancyFromGuard$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(vacancySearchExistsGuardActions.canActivate),
      // CDH-1291 - do not provide accountId for this call
      // we search the vacancies in all workspaces to potentially auto-select the workspace associated to the vacancy
      map(({ vacancyId }) => [{ query: { filter: `id==${vacancyId}` } }, undefined] as const),
      this.fetchVacancies(
        vacancyApiActions.searchVacancyFromGuardSuccess,
        vacancyApiActions.searchVacancyFromGuardFailure
      )
    );
  });

  readonly loadVacanciesByIds$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BoDashboardActions.enter),
      filter(({ vacancyIds }) => vacancyIds.length > 0),
      switchMap(({ vacancyIds }) => {
        const filter = getFilterFromVacancyIds(vacancyIds.map(toUUID));
        return this.vacancySvc.list({ filter }).pipe(
          map(({ _embedded }) =>
            vacancyApiActions.loadVacanciesByIdsSuccess({ vacancies: _embedded?.results ?? [] })
          ),
          catchError((error: unknown) =>
            of(vacancyApiActions.loadVacanciesByIdsFailure({ error: getVacancyError(error) }))
          )
        );
      })
    );
  });

  // From Listing Page
  deleteDraftVacancy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(listVacancyActions.deleteDraftVacancy),
      mergeMap(({ id }) =>
        this.vacancyDetailResource.deleteVacancy(toUUID(id)).pipe(
          take(1),
          map((id) => vacancyApiActions.deleteDraftVacancySuccess({ id })),
          catchError((error: unknown) =>
            of(vacancyApiActions.deleteDraftVacancyFailure({ error: getVacancyError(error) }))
          )
        )
      )
    )
  );

  deleteDraftVacancySuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(vacancyApiActions.deleteDraftVacancySuccess),
        tap(() => this.notify(`VACANCY_PAGE.MESSAGE.DELETE_DRAFT`)),
        tap(() => this.workspaceRoute.navigate([AppConfig.routes.vacancies]))
      ),
    { dispatch: false }
  );

  deleteDraftVacancyFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(vacancyApiActions.deleteDraftVacancyFailure),
        tap(() => this.notify(`VACANCY_PAGE.MESSAGE.ERROR_DELETE_DRAFT`, ActionState.Error))
      ),
    { dispatch: false }
  );

  private notify(key: string, state = ActionState.Success): void {
    const message = this.translateSvc.instant(key);
    this.notificationSvc.show(message, { state });
  }

  private updateVacancySuccessOperator(): (
    source: Observable<Vacancy>
  ) => Observable<{ id: UUID; changes: Partial<Vacancy> }> {
    return (source) =>
      source.pipe(
        tap((vacancy) => this.boDashboardService.addVacancy(vacancy)),
        map((vacancy: Vacancy) => {
          const {
            applicationCount: _applicationCount,
            newApplicant: _newApplicant,
            ...changes
          } = vacancy;
          return { id: vacancy.id, changes };
        })
      );
  }

  private fetchVacancies(
    successAction: FetchVacanciesSuccessActionCreator,
    failureAction: FetchVacanciesFailureActionCreator
  ): OperatorFunction<
    readonly [{ query: QueryOptions }, string | undefined],
    FetchVacanciesAction
  > {
    return switchMap(([{ query }, accountId]) => {
      const options = query || { limit: 25 };
      return this.vacancySvc.list(options, accountId).pipe(
        map(({ _embedded, _links, totalCount, filter }) =>
          successAction({
            vacancies:
              _embedded?.results.map((vacancy) => VacancySerializer.fromJson(vacancy)) ?? [],
            _links,
            totalCount,
            filter,
          })
        ),
        catchError((error: unknown) => of(failureAction({ error: getVacancyError(error) })))
      );
    });
  }
}

type FetchVacanciesSuccessActionCreator =
  | typeof vacancyApiActions.listVacanciesSuccess
  | typeof vacancyApiActions.loadMoreVacanciesSuccess
  | typeof vacancyApiActions.searchVacancyFromGuardSuccess;
type FetchVacanciesFailureActionCreator =
  | typeof vacancyApiActions.listVacanciesFailure
  | typeof vacancyApiActions.loadMoreVacanciesFailure
  | typeof vacancyApiActions.searchVacancyFromGuardFailure;
type FetchVacanciesAction = ReturnType<
  FetchVacanciesSuccessActionCreator | FetchVacanciesFailureActionCreator
>;

const getVacancyError = (error: unknown): MessageError => getMessageError(error, 'VacancyEffects');

const getFilterFromVacancyIds = (vacancyIds: Vacancy['id'][]): string =>
  `id==${vacancyIds.join(`,id==`)}`;
